<template>
	<div class="overflow-hidden relative z-0" :style="coverSize" ref="container">
		<div v-if="obscure" class="obscure absolute height-full width-full z-3"></div>
		<div
			role="img"
			:src="src"
			class="absolute height-full width-full z-2"
			:class="[
				{
					'd-flex align-center justify-center': centerSlotContent,
					'inner-border': displayInnerBorder,
				},
				roundedClass,
			]"
			:style="coverImage"
		>
			<slot />
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, ref, onMounted } from 'vue'
	import { ImageService } from '~/passporter-services/shared/image'
	import DEFAULT_IMAGE from '~/components/passporterUi/images/brokenImage.png'

	interface Props {
		src?: string
		height?: string
		width?: string
		isVisible?: boolean
		lazy?: boolean
		centerSlotContent?: boolean
		brightnessFilterValue?: number
		previewOnServer?: boolean
		obscure?: boolean
		backupImage?: string
		displayInnerBorder?: boolean
		roundedClass?: string
	}

	const isValid = ref<boolean>(true)

	onMounted(async () => {
		if (!props.src) return
		isValid.value = await checkImageIsValid(props.src)
	})

	const props = withDefaults(defineProps<Props>(), {
		src: '',
		height: '100%',
		width: '100%',
		isVisible: true,
		brightnessFilterValue: 0,
		backupImage: DEFAULT_IMAGE,
		roundedClass: '',
	})

	const container = ref<HTMLElement>()

	const coverSize = computed(() => {
		return {
			height: props.height,
			minHeight: props.height,
			width: props.width,
			minWidth: props.width,
		}
	})

	const coverImage = computed((): Record<string, string> => {
		const box = container.value
		let width
		let height

		if (props.previewOnServer) {
			width = 350
			height = 350
		}

		if (box) {
			width = box.offsetWidth
			height = box.offsetHeight
		}
		if (!width || !height) return {}

		const scale = 2

		let image = props.backupImage
		if (props.src && isValid.value)
			image = ImageService.resizeImage(props.src, width, height, scale)!

		let result: Record<string, string> = {
			backgroundImage: `url("${image}")`,
			backgroundRepeat: 'no-repeat',
			backgroundSize: 'cover',
			backgroundPosition: 'center center',
		}
		if (props.brightnessFilterValue) result.filter = `brightness(${props.brightnessFilterValue})`
		return result
	})

	function checkImageIsValid(url: string): Promise<boolean> {
		const img = new Image()
		img.src = url
		return new Promise((resolve) => {
			img.onload = () => resolve(true)
			img.onerror = () => resolve(false)
		})
	}
</script>

<style scoped>
	.obscure {
		background: linear-gradient(180deg, rgba(18, 18, 18, 0) 0, rgba(18, 18, 18, 0.3) 100%);
	}

	.inner-border {
		-webkit-box-shadow: inset 0px 0px 0px 2px #1018281a;
		-moz-box-shadow: inset 0px 0px 0px 2px #1018281a;
		box-shadow: inset 0px 0px 0px 2px #1018281a;
	}
</style>
